import { useTranslation } from "react-i18next";
import {
  HiOutlineInformationCircle,
  HiOutlineExclamationTriangle,
  HiOutlineCheckCircle,
  HiOutlineSquares2X2,
} from "react-icons/hi2";
import { motion } from "framer-motion";
import {
  AssessmentScoringResult,
  CriteriaBreakdown,
} from "types/assessmentTypes";
import { getDisplayPercent, isCriteriaNegative } from "./helper/reportHelper";
import { useScope } from "hooks/useScope";
import { useMemo } from "react";

interface ScoringResultsProps {
  scoringResults: AssessmentScoringResult[];
  rowsPerPage?: number;
  pageIndex?: number;
  scopeOverride?: "user" | "admin";
  compact?: boolean;
}

const PAEI_CRITERIASINDEX = [25, 28, 26, 27];

export default function ScoringResultsTable({
  scoringResults,
  rowsPerPage,
  pageIndex = 0,
  scopeOverride,
  compact = false,
}: ScoringResultsProps) {
  const { t } = useTranslation();
  const scopeFromHook = useScope();
  const scope = scopeOverride ?? scopeFromHook;

  if (!scoringResults || scoringResults.length === 0) {
    return null;
  }

  const result = scoringResults[0];
  const criteriaBreakdown = result.criteriaBreakdown as CriteriaBreakdown[];

  const getKpiLabel = (code: string) => {
    const labels: Record<string, string> = {
      REAL_REVENUE_GROWTH: t("assessment.kpis.REAL_REVENUE_GROWTH"),
      OPERATING_MARGIN: t("assessment.kpis.OPERATING_MARGIN"),
      CAPACITY_UTILIZATION: t("assessment.kpis.CAPACITY_UTILIZATION"),
      CCC: t("assessment.kpis.CCC"),
      ROA: t("assessment.kpis.ROA"),
      EMPLOYEE_TURNOVER: t("assessment.kpis.EMPLOYEE_TURNOVER"),
      LOGISTICS_TO_SALES: t("assessment.kpis.LOGISTICS_TO_SALES"),
      RETURN_RATE: t("assessment.kpis.RETURN_RATE"),
      RND_TO_SALES: t("assessment.kpis.RND_TO_SALES"),
      RENEWABLE_ENERGY_SHARE: t("assessment.kpis.RENEWABLE_ENERGY_SHARE"),
      IP_POWER: t("assessment.kpis.IP_POWER"),
      ORGANIZATION_SIZE: t("assessment.kpis.ORGANIZATION_SIZE"),
    };
    return labels[code] || code;
  };

  const getCriteriaStatus = (criteria: CriteriaBreakdown) => {
    const displayPercent = getDisplayPercent(criteria);

    let status: "needsImprovement" | "average" | "good";

    if (displayPercent < 40) {
      status = "needsImprovement";
    } else if (displayPercent <= 60) {
      status = "average";
    } else {
      status = "good";
    }

    const statusMap = {
      needsImprovement: {
        label: t("assessment.report.status.needsImprovement"),
        color: "text-red-600 dark:text-red-400",
        bgColor: "bg-red-50 dark:bg-red-900/20",
        borderColor: "border-red-200 dark:border-red-800",
        icon: HiOutlineExclamationTriangle,
      },
      average: {
        label: t("assessment.report.status.average"),
        color: "text-yellow-600 dark:text-yellow-400",
        bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
        borderColor: "border-yellow-200 dark:border-yellow-800",
        icon: HiOutlineInformationCircle,
      },
      good: {
        label: t("assessment.report.status.good"),
        color: "text-green-600 dark:text-green-400",
        bgColor: "bg-green-50 dark:bg-green-900/20",
        borderColor: "border-green-200 dark:border-green-800",
        icon: HiOutlineCheckCircle,
      },
    };

    return statusMap[status];
  };

  const sortedCriteria = [...criteriaBreakdown].sort(
    (a, b) => a.criteriaIndex - b.criteriaIndex
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const paginatedCriteria = useMemo(() => {
    if (!rowsPerPage || rowsPerPage <= 0) return sortedCriteria;
    const start = pageIndex * rowsPerPage;
    return sortedCriteria.slice(start, start + rowsPerPage);
  }, [sortedCriteria, rowsPerPage, pageIndex]);

  return (
    <div className="mt-6 rounded-2xl bg-gray-100/50 shadow-lg dark:bg-navy-800">
      <div className="border-b border-gray-200 p-4 dark:border-navy-700">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 p-2.5 shadow-lg shadow-brand-500/25">
            <HiOutlineSquares2X2 className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">
              {t("assessment.report.scoringResults")}
            </h3>
            <p className="text-xs text-gray-700 dark:text-gray-400">
              {t("assessment.report.scoringResultsDesc")}
            </p>
          </div>
        </div>
      </div>

      <div className={`${compact ? "p-2" : "p-4"} overflow-x-auto`}>
        <div className="w-full">
          <div
            className={`mb-3 grid ${
              scope === "admin" ? "grid-cols-11" : "grid-cols-7"
            } gap-2 rounded-lg bg-brand-100/50 ${
              compact ? "p-1" : "p-2"
            } dark:bg-navy-700`}
          >
            <div className="col-span-1 px-2 text-center">
              <span className="text-xs font-semibold text-gray-800 dark:text-gray-300">
                {t("table.row")}
              </span>
            </div>
            <div
              className={`${
                scope === "admin" ? "col-span-4" : "col-span-2"
              } px-2 text-start`}
            >
              <span className="text-xs font-semibold text-gray-800 dark:text-gray-300">
                {t("assessment.report.criteria")}
              </span>
            </div>
            {scope === "admin" && (
              <div className="col-span-1 px-2 text-center">
                <span className="text-xs font-semibold text-gray-800 dark:text-gray-300">
                  {t("assessment.report.likertPercent")}
                  <br />
                  <span dir="ltr">(0 - 100)</span>
                </span>
              </div>
            )}
            {scope === "admin" && (
              <div className="col-span-1 px-2 text-center">
                <span className="text-xs font-semibold text-gray-800 dark:text-gray-300">
                  {t("assessment.report.objectivePercent")}
                  <br />
                  <span dir="ltr">(0 - 100)</span>
                </span>
              </div>
            )}
            <div className="col-span-1 px-2 text-center">
              <span className="text-xs font-semibold text-gray-800 dark:text-gray-300">
                {t("assessment.report.correctionFactor")}
                <br />
                <span dir="ltr">(0.5 - 2)</span>
              </span>
            </div>
            <div className="col-span-1 px-2 text-center">
              <span className="text-xs font-semibold text-gray-800 dark:text-gray-300">
                {t("assessment.report.correctedPercent")}
                <br />
                <span dir="ltr">(0 - 100)</span>
              </span>
            </div>
            <div className="col-span-2 px-2 text-center">
              <span className="text-xs font-semibold text-gray-800 dark:text-gray-300">
                {t("assessment.report.interpretations")}
              </span>
            </div>
          </div>

          <div className="space-y-1">
            {paginatedCriteria.map((criteria, index) => {
              const isNegative = isCriteriaNegative(criteria);
              const displayPercent = getDisplayPercent(criteria);
              const status = getCriteriaStatus(criteria);
              const StatusIcon = status.icon;
              const kpiNames = criteria.kpis
                .map((kpi) => getKpiLabel(kpi.code))
                .join("، ");

              const isBelow40 =
                criteria.objectivePercent < 40 &&
                !PAEI_CRITERIASINDEX.includes(criteria.criteriaIndex);

              const rowNumber =
                (rowsPerPage ? pageIndex * rowsPerPage : 0) + index + 1;

              return (
                <motion.div
                  key={criteria.criteriaIndex}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.02 }}
                  className={`grid ${
                    scope === "admin" ? "grid-cols-11" : "grid-cols-7"
                  } gap-2 rounded-lg ${
                    compact ? "px-1 py-[2px]" : "p-2"
                  } transition-colors ${
                    index % 2 === 0
                      ? "bg-white dark:bg-navy-800"
                      : "bg-indigo-100/50 dark:bg-navy-700/50"
                  } hover:bg-brand-50/50 dark:hover:bg-brand-900/10 ${
                    isBelow40 && scope === "admin"
                      ? "border-l-4 border-l-amber-400"
                      : ""
                  }`}
                >
                  <div className="col-span-1 flex items-center justify-center px-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {rowNumber}
                    </span>
                  </div>

                  <div
                    className={`${
                      scope === "admin" ? "col-span-4" : "col-span-2"
                    } flex items-center px-2`}
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-navy-700 dark:text-white">
                          {t(`criteria.${criteria.criteriaIndex}`)}
                        </span>
                        {/* <span className="text-xs font-medium text-gray-800 dark:text-gray-300">
                          ( {criteria.criteriaIndex} )
                        </span> */}
                        {scope === "admin" && isNegative && (
                          <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[8px] font-bold text-red-600 dark:bg-red-900/30 dark:text-red-400">
                            {t("assessment.report.kpiType.negative")}
                          </span>
                        )}
                        {scope === "admin" && isBelow40 && (
                          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[8px] font-bold text-amber-600 dark:bg-amber-900/30 dark:text-amber-400">
                            ⚠️ {t("assessment.report.belowThreshold")}
                          </span>
                        )}
                      </div>
                      {!compact && criteria.kpis.length > 0 && (
                        <span className="text-[10px] text-gray-700 dark:text-gray-300">
                          {t("assessment.report.impactingKpis")} : {kpiNames}
                        </span>
                      )}
                    </div>
                  </div>

                  {scope === "admin" && (
                    <div className="col-span-1 flex items-center justify-center px-2">
                      <span className="text-sm font-medium text-navy-700 dark:text-white">
                        {criteria.likertPercent.toFixed(1)} %
                      </span>
                    </div>
                  )}

                  {scope === "admin" && (
                    <div className="col-span-1 flex items-center justify-center px-2">
                      <span className="text-center text-sm text-gray-800 dark:text-gray-300">
                        {criteria.objectivePercent.toFixed(1)} %
                      </span>
                    </div>
                  )}

                  <div className="col-span-1 flex items-center justify-center px-2">
                    <span className="text-center text-sm font-medium text-brand-600 dark:text-brand-200">
                      {criteria.correctionFactor.toFixed(2)}
                    </span>
                  </div>

                  <div className="col-span-1 flex flex-col items-center justify-center gap-1 px-2">
                    <span
                      className={`text-center text-sm font-bold ${status.color}`}
                    >
                      {displayPercent.toFixed(1)} %
                    </span>
                    {scope === "admin" && isNegative && (
                      <span className="text-center text-[10px] text-gray-400 line-through dark:text-gray-500">
                        {t("assessment.report.originalValue")} : <br />
                        {criteria.correctedPercent.toFixed(1)} %
                      </span>
                    )}
                  </div>

                  <div className="col-span-2 flex items-center justify-center px-2">
                    <span
                      className={`flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${status.bgColor} ${status.color}`}
                    >
                      <StatusIcon className="h-3 w-3" />
                      {status.label}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-4 grid grid-cols-3 gap-4 rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
            <div className="flex items-center gap-2">
              <div className="flex h-3 w-3 rounded-full bg-red-500" />
              <span className="text-xs text-gray-800 dark:text-gray-300">
                {t("assessment.report.legend.needsImprovement")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex h-3 w-3 rounded-full bg-yellow-500" />
              <span className="text-xs text-gray-800 dark:text-gray-300">
                {t("assessment.report.legend.average")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex h-3 w-3 rounded-full bg-green-500" />
              <span className="text-xs text-gray-800 dark:text-gray-300">
                {t("assessment.report.legend.good")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
